import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Check, Phone, ShieldCheck, Clock, Gift, UtensilsCrossed, ShoppingBag, Apple, Scissors, Shirt, Building2, Store, Monitor, Wallet, Tablet, Smartphone, ShoppingCart, Mail, User, MapPin, StickyNote, Sparkles, PartyPopper } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { validateLeadForm } from '@/lib/formValidation';
import { trackLead } from '@/lib/tracking';
import Navbar from '@/components/smartpos/Navbar';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import { useLanguage } from '@/lib/LanguageContext';

const TOTAL_STEPS = 4;

export default function Demo() {
  const { t } = useLanguage();
  useDocumentMeta({
    title: 'Demo Gratuita — SmartPOS | Prueba el TPV sin compromiso',
    description: 'Solicita tu demo gratuita de SmartPOS. Un experto te llama en menos de 24h laborables para personalizar la demo perfecta para tu negocio.',
    canonicalPath: '/demo',
  });

  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [sector, setSector] = useState('');
  const [locations, setLocations] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', company: '', province: '', notes: '' });
  const [honeypot, setHoneypot] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const sectors = [
    { label: t('lead.sector.hospitality'), value: 'hosteleria', icon: UtensilsCrossed },
    { label: t('lead.sector.retail'), value: 'retail', icon: ShoppingBag },
    { label: t('lead.sector.food'), value: 'alimentacion', icon: Apple },
    { label: t('lead.sector.beauty'), value: 'belleza', icon: Scissors },
    { label: t('lead.sector.fashion'), value: 'moda', icon: Shirt },
    { label: t('lead.sector.other'), value: 'otro', icon: Building2 },
  ];

  const locationsOptions = [
    { label: '1', value: '1', icon: Store },
    { label: '2-5', value: '2-5', icon: Store },
    { label: '6-20', value: '6-20', icon: Building2 },
    { label: '+20', value: '20+', icon: Building2 },
  ];

  const products = [
    { label: t('lead.product.software'), value: 'software', icon: Monitor },
    { label: t('lead.product.hardware'), value: 'hardware', icon: Smartphone },
    { label: t('lead.product.cashdro'), value: 'cashdro', icon: Wallet },
    { label: t('lead.product.kiosk'), value: 'quiosco', icon: Tablet },
    { label: t('lead.product.menu'), value: 'carta', icon: Smartphone },
    { label: t('lead.product.orders'), value: 'pedidos', icon: ShoppingCart },
  ];

  useEffect(() => {
    const handler = (e) => {
      if (e.detail) setFormData(d => ({ ...d, phone: e.detail }));
    };
    window.addEventListener('prefillLeadPhone', handler);
    return () => window.removeEventListener('prefillLeadPhone', handler);
  }, []);

  const toggleProduct = (value) => {
    setSelectedProducts(prev =>
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    );
  };

  const updateField = (key, value) => {
    setFormData(d => ({ ...d, [key]: value }));
    setFieldErrors(prev => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const inputClass = (key) =>
    `w-full bg-gray-50 border text-gray-900 placeholder-gray-300 px-4 py-3 rounded-xl text-base focus:outline-none focus:bg-white transition-colors ${
      fieldErrors[key] ? 'border-red-300 focus:border-red-400' : 'border-gray-200 focus:border-brand-blue'
    }`;

  const fieldError = (key) =>
    fieldErrors[key] ? (<p className="mt-1.5 text-xs font-medium text-red-600">{t(fieldErrors[key])}</p>) : null;

  const canAdvance = () => {
    if (step === 0) return !!sector;
    if (step === 1) return !!locations;
    if (step === 2) return selectedProducts.length > 0;
    return true;
  };

  const handleNext = () => {
    if (step < TOTAL_STEPS - 1) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setSubmitError('');
    const errors = validateLeadForm(formData);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      const firstKey = Object.keys(errors)[0];
      requestAnimationFrame(() => document.getElementById(`demo-${firstKey}`)?.focus());
      setSubmitting(false);
      return;
    }
    setFieldErrors({});
    try {
      await base44.functions.invoke('createLead', {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        company: formData.company,
        province: formData.province,
        notes: formData.notes,
        website: honeypot,
        sector,
        locations,
        products: selectedProducts,
        source: 'demo-page',
      });
      setSubmitted(true);
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'conversion', {
          send_to: 'AW-18410505170/_kr3CLLu6uccENKH6MpE',
        });
      }
      trackLead({ sector, locations, products_count: selectedProducts.length, source: 'demo-page' });
    } catch {
      // Un envío repetido del mismo teléfono responde igual que un alta correcta y
      // no se distingue desde el navegador: aquí solo caen los fallos reales
      // (límite de peticiones o un error puntual del servidor).
      setSubmitError(t('demo.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  };

  const progress = (step / (TOTAL_STEPS - 1)) * 100;

  const stepTitles = [
    { icon: UtensilsCrossed, title: t('demo.s0Title'), subtitle: t('demo.s0Subtitle') },
    { icon: Store, title: t('demo.s1Title'), subtitle: t('demo.s1Subtitle') },
    { icon: Sparkles, title: t('demo.s2Title'), subtitle: t('demo.s2Subtitle') },
    { icon: Phone, title: t('demo.s3Title'), subtitle: t('demo.s3Subtitle') },
  ];

  const reassurances = [
    { icon: ShieldCheck, text: t('demo.reassurance1') },
    { icon: Clock, text: t('demo.reassurance2') },
    { icon: Gift, text: t('demo.reassurance3') },
  ];

  const successItems = [t('demo.successItem1'), t('demo.successItem2'), t('demo.successItem3')];

  return (
    <div className="min-h-screen flex flex-col font-body bg-white">
      <Navbar />

      {/* Progress bar */}
      {!submitted && (
        <div className="shrink-0 max-w-2xl w-full mx-auto px-6 pt-36 pb-2">
          <div className="flex justify-between text-gray-400 text-xs font-medium mb-2">
            <span>{t('demo.step')} {step + 1} {t('demo.of')} {TOTAL_STEPS}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden" role="progressbar" aria-valuenow={Math.round(progress)} aria-valuemin={0} aria-valuemax={100} aria-label={t('demo.progressLabel')}>
            <div className="h-full brand-gradient rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      {/* Main content */}
      <main id="contenido-principal" className="flex-1 flex items-center justify-center min-h-0 overflow-y-auto">
        <div className="w-full max-w-2xl mx-auto px-6 py-4 pb-10">
          {!submitted ? (
            <>
              {/* Step header */}
              <div className="text-center mb-5">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl mb-3" style={{ background: 'linear-gradient(135deg, #0095fc15, #7800f715)' }}>
                  {(() => {
                    const StepIcon = stepTitles[step].icon;
                    return <StepIcon size={22} className="text-brand-violet" />;
                  })()}
                </div>
                <h1 className="font-heading font-semibold text-xl lg:text-2xl text-gray-900 mb-1 tracking-tight">
                  {stepTitles[step].title}
                </h1>
                <p className="text-gray-400 text-sm">{stepTitles[step].subtitle}</p>
              </div>

              {/* Step 0: Sector */}
              {step === 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {sectors.map(opt => {
                    const Icon = opt.icon;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setSector(opt.value)}
                        aria-pressed={sector === opt.value}
                        className={`p-4 rounded-2xl border text-center transition-colors duration-150 flex flex-col items-center gap-2 ${
                          sector === opt.value
                            ? 'border-brand-blue bg-blue-50 text-brand-blue'
                            : 'border-gray-200 hover:border-brand-blue/40 hover:bg-gray-50 text-gray-600'
                        }`}
                      >
                        <Icon size={24} className={sector === opt.value ? 'text-brand-blue' : 'text-gray-400'} />
                        <p className="text-sm font-bold">{opt.label}</p>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Step 1: Locations */}
              {step === 1 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {locationsOptions.map(opt => {
                    const Icon = opt.icon;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setLocations(opt.value)}
                        aria-pressed={locations === opt.value}
                        className={`p-4 rounded-2xl border text-center transition-colors duration-150 flex flex-col items-center gap-2 ${
                          locations === opt.value
                            ? 'border-brand-blue bg-blue-50 text-brand-blue'
                            : 'border-gray-200 hover:border-brand-blue/40 hover:bg-gray-50 text-gray-600'
                        }`}
                      >
                        <Icon size={22} className={locations === opt.value ? 'text-brand-blue' : 'text-gray-400'} />
                        <p className="text-lg font-bold">{opt.label}</p>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Step 2: Products */}
              {step === 2 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {products.map(opt => {
                    const Icon = opt.icon;
                    const selected = selectedProducts.includes(opt.value);
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => toggleProduct(opt.value)}
                        aria-pressed={selectedProducts.includes(opt.value)}
                        className={`p-4 rounded-2xl border text-center transition-colors duration-150 flex flex-col items-center gap-2 relative ${
                          selected
                            ? 'border-brand-blue bg-blue-50 text-brand-blue'
                            : 'border-gray-200 hover:border-brand-blue/40 hover:bg-gray-50 text-gray-600'
                        }`}
                      >
                        {selected && (
                          <div className="absolute top-2 right-2 w-5 h-5 brand-gradient rounded-full flex items-center justify-center">
                            <Check size={11} className="text-white" strokeWidth={3} />
                          </div>
                        )}
                        <Icon size={24} className={selected ? 'text-brand-blue' : 'text-gray-400'} />
                        <p className="text-sm font-bold">{opt.label}</p>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Step 3: Contact form */}
              {step === 3 && (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <input
                    type="text"
                    name="website"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="absolute opacity-0 pointer-events-none w-px h-px -left-[9999px]"
                  />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="demo-name" className="text-gray-500 text-xs font-bold mb-1.5 block uppercase tracking-wide flex items-center gap-1.5">
                        <User size={12} /> {t('demo.name')}
                      </label>
                      <input
                        id="demo-name"
                        type="text"
                        required
                        inputMode="text"
                        autoComplete="name"
                        enterKeyHint="next"
                        placeholder={t('demo.namePlaceholder')}
                        value={formData.name}
                        onChange={e => updateField('name', e.target.value)}
                        aria-invalid={!!fieldErrors.name}
                        className={inputClass('name')}
                      />
                      {fieldError('name')}
                    </div>
                    <div>
                      <label htmlFor="demo-phone" className="text-gray-500 text-xs font-bold mb-1.5 block uppercase tracking-wide flex items-center gap-1.5">
                        <Phone size={12} /> {t('demo.phone')}
                      </label>
                      <input
                        id="demo-phone"
                        type="tel"
                        required
                        inputMode="tel"
                        autoComplete="tel"
                        enterKeyHint="next"
                        placeholder={t('demo.phonePlaceholder')}
                        value={formData.phone}
                        onChange={e => updateField('phone', e.target.value)}
                        aria-invalid={!!fieldErrors.phone}
                        className={inputClass('phone')}
                      />
                      {fieldError('phone')}
                    </div>
                  </div>
                  <div>
                    <label htmlFor="demo-email" className="text-gray-500 text-xs font-bold mb-1.5 block uppercase tracking-wide flex items-center gap-1.5">
                      <Mail size={12} /> {t('demo.email')}
                    </label>
                    <input
                      id="demo-email"
                      type="email"
                      required
                      inputMode="email"
                      autoComplete="email"
                      enterKeyHint="next"
                      placeholder={t('demo.emailPlaceholder')}
                      value={formData.email}
                      onChange={e => updateField('email', e.target.value)}
                      aria-invalid={!!fieldErrors.email}
                      className={inputClass('email')}
                    />
                    {fieldError('email')}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="demo-company" className="text-gray-500 text-xs font-bold mb-1.5 block uppercase tracking-wide flex items-center gap-1.5">
                        <Building2 size={12} /> {t('demo.company')}
                      </label>
                      <input
                        id="demo-company"
                        type="text"
                        required
                        inputMode="text"
                        autoComplete="organization"
                        enterKeyHint="next"
                        placeholder={t('demo.companyPlaceholder')}
                        value={formData.company}
                        onChange={e => updateField('company', e.target.value)}
                        aria-invalid={!!fieldErrors.company}
                        className={inputClass('company')}
                      />
                      {fieldError('company')}
                    </div>
                    <div>
                      <label htmlFor="demo-province" className="text-gray-500 text-xs font-bold mb-1.5 block uppercase tracking-wide flex items-center gap-1.5">
                        <MapPin size={12} /> {t('demo.province')}
                      </label>
                      <input
                        id="demo-province"
                        type="text"
                        required
                        inputMode="text"
                        autoComplete="address-level2"
                        enterKeyHint="next"
                        placeholder={t('demo.provincePlaceholder')}
                        value={formData.province}
                        onChange={e => updateField('province', e.target.value)}
                        aria-invalid={!!fieldErrors.province}
                        className={inputClass('province')}
                      />
                      {fieldError('province')}
                    </div>
                  </div>
                  <div>
                    <label htmlFor="demo-notes" className="text-gray-500 text-xs font-bold mb-1.5 block uppercase tracking-wide flex items-center gap-1.5">
                      <StickyNote size={12} /> {t('demo.notes')}
                    </label>
                    <textarea
                      id="demo-notes"
                      rows={3}
                      maxLength={1000}
                      placeholder={t('demo.notesPlaceholder')}
                      value={formData.notes}
                      onChange={e => updateField('notes', e.target.value)}
                      aria-invalid={!!fieldErrors.notes}
                      className={`${inputClass('notes')} resize-none`}
                    />
                    {fieldError('notes')}
                  </div>
                  {submitError && (
                    <div role="alert" className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-red-50 border border-red-200">
                      <span className="text-red-600 text-sm font-medium">{submitError}</span>
                    </div>
                  )}
                </form>
              )}

              {/* Navigation buttons */}
              <div className="flex items-center gap-3 mt-5">
                {step > 0 && (
                  <button
                    type="button"
                    onClick={handleBack}
                    className="flex items-center gap-1.5 px-5 py-3.5 rounded-xl text-gray-500 hover:text-gray-700 hover:bg-gray-50 font-bold text-sm transition-colors"
                  >
                    <ArrowLeft size={16} /> {t('demo.back')}
                  </button>
                )}
                {step < TOTAL_STEPS - 1 ? (
                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={!canAdvance()}
                    className="flex-1 bg-brand-blue text-white py-3.5 rounded-xl font-bold text-base disabled:opacity-40 hover:opacity-90 transition-opacity flex items-center justify-center gap-2 shadow-lg shadow-blue-500/20"
                  >
                    {t('demo.continue')} <ArrowRight size={18} />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="flex-1 bg-brand-blue text-white py-3.5 rounded-xl font-bold text-base hover:opacity-90 transition-opacity flex items-center justify-center gap-2 shadow-lg shadow-blue-500/20 disabled:opacity-60"
                  >
                    {submitting ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> {t('demo.submitting')}
                      </>
                    ) : (
                      <>{t('demo.submit')} <ArrowRight size={18} /></>
                    )}
                  </button>
                )}
              </div>

              {/* Reassurances */}
              <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-4">
                {reassurances.map(r => (
                  <div key={r.text} className="flex items-center gap-1.5">
                    <r.icon size={14} className="text-brand-blue" />
                    <p className="text-gray-400 text-xs font-medium">{r.text}</p>
                  </div>
                ))}
              </div>
            </>
          ) : (
            /* Success state */
            <div role="status" aria-live="polite" className="text-center">
              <div className="w-16 h-16 brand-gradient rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-500/30">
                <PartyPopper size={28} className="text-white" />
              </div>
              <h1 className="font-heading font-semibold text-2xl text-gray-900 mb-2 tracking-tight">{t('demo.successTitle1')} {formData.name || ''}{t('demo.successTitle2')}</h1>
              <p className="text-gray-500 text-base font-light mb-4">
                {t('demo.successDesc1')} <strong className="text-gray-900">{formData.phone}</strong> {t('demo.successDesc2')}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mb-5">
                {successItems.map(item => (
                  <div key={item} className="flex items-center gap-1.5">
                    <Check size={14} className="text-brand-green" />
                    <p className="text-gray-500 text-xs font-medium">{item}</p>
                  </div>
                ))}
              </div>
              <p className="text-gray-400 text-sm mb-4">
                {t('demo.successCall')} <a href="tel:+34936302521" className="text-brand-blue font-bold hover:underline">+34 936 302 521</a>
              </p>
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center gap-2 text-gray-600 hover:text-brand-blue font-bold text-sm transition-colors"
              >
                {t('demo.backHome')} <ArrowRight size={16} />
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}